import {
  Book,
  Briefcase,
  Circle,
  CircleDollarSign,
  Clock,
  Coffee,
  Dumbbell,
  Footprints,
  Gamepad2,
  GraduationCap,
  Heart,
  House,
  Laptop,
  Leaf,
  type LucideIcon,
  Music,
  Paintbrush,
  Plane,
  ShoppingCart,
  Sparkles,
  Star,
  UtensilsCrossed,
} from "lucide-react";

/**
 * Renders an activity's icon on the web.
 *
 * Activities store an SF Symbol name — the Apple apps pick from the curated set in
 * SharedModels/ActivityAppearance.swift. SF Symbols themselves are licensed for use
 * on Apple platforms only and cannot be shipped to the web, so the web maps each
 * name to the closest icon from lucide (MIT). Only the name crosses the boundary;
 * no Apple artwork is redistributed.
 *
 * Keep this map in step with `ActivityPalette.icons` when the picker gains a symbol.
 */
const SF_SYMBOL_TO_LUCIDE: Record<string, LucideIcon> = {
  "figure.run": Footprints,
  book: Book,
  "fork.knife": UtensilsCrossed,
  briefcase: Briefcase,
  cart: ShoppingCart,
  heart: Heart,
  "dollarsign.circle": CircleDollarSign,
  clock: Clock,
  gamecontroller: Gamepad2,
  "music.note": Music,
  airplane: Plane,
  graduationcap: GraduationCap,
  leaf: Leaf,
  "cup.and.saucer": Coffee,
  dumbbell: Dumbbell,
  paintbrush: Paintbrush,
  // Seen in existing data but not in the picker's curated set.
  house: House,
  laptopcomputer: Laptop,
  sparkles: Sparkles,
  star: Star,
};

/** Rendered when a name is outside the map, so an unknown symbol never blanks the row. */
const FALLBACK_ICON = Circle;

/** SF Symbol variant suffixes that carry no meaning for us — `heart.fill` is a heart. */
const VARIANT_SUFFIXES = [".fill", ".circle.fill"];

function iconFor(name: string): LucideIcon {
  const exact = SF_SYMBOL_TO_LUCIDE[name];
  if (exact) return exact;

  for (const suffix of VARIANT_SUFFIXES) {
    if (!name.endsWith(suffix)) continue;
    const base = SF_SYMBOL_TO_LUCIDE[name.slice(0, -suffix.length)];
    if (base) return base;
  }
  return FALLBACK_ICON;
}

type ActivityIconProps = {
  /** SF Symbol name as stored on the activity, e.g. "heart.fill". */
  name: string;
  /** Edge length in px. */
  size?: number;
};

const DEFAULT_SIZE = 20;
const STROKE_WIDTH = 1.75;

export function ActivityIcon({ name, size = DEFAULT_SIZE }: ActivityIconProps) {
  const Glyph = iconFor(name);
  return <Glyph size={size} strokeWidth={STROKE_WIDTH} aria-hidden="true" focusable="false" />;
}
